// =================================================================
// Engine: PK/PD targets — AUC, Cmax/MIC (PSD §4.2.12 / TODO Task 3.2)
// =================================================================
import { err, ok } from './types';
/**
 * AUC₀–24, AUC/MIC, Cmax/MIC (PSD §4.2.12).
 * AUC_tau = dose/(Vd×kel); AUC24 = AUC_tau × (24/interval); Cmax/MIC ≥ 8–10 pass.
 */
export function computePKPD(doseMg, kelPerHr, vdL, intervalHr, infusionDurationHr, mic, aucMicTarget = 100) {
    if (!(mic > 0)) {
        return err('mic-must-be-positive', 'MIC must be positive.', 'mic');
    }
    if (!(kelPerHr > 0)) {
        return err('kel-must-be-positive', 'Kel must be positive.', 'kelPerHr');
    }
    const k = kelPerHr;
    const t = infusionDurationHr;
    const cmax = (doseMg / (vdL * k * t)) * ((1 - Math.exp(-k * t)) / (1 - Math.exp(-k * intervalHr)));
    const aucTau = doseMg / (vdL * k);
    const auc24 = aucTau * (24 / intervalHr);
    const aucMic = auc24 / mic;
    const cmaxMic = cmax / mic;
    return ok({
        auc24,
        aucMic,
        cmaxMic,
        cmax,
        cmaxPass: cmaxMic >= 8,
        aucPass: aucMic >= aucMicTarget,
    });
}
/**
 * Steady-state attainment estimate (FE-E8).
 * ssApproxTimeHr = halfLivesToSS × halfLifeHr; ssDoseNumber = ceil(time / intervalHr).
 * Default 5 half-lives is the standard pharmacokinetic definition of SS.
 */
export function computeSteadyStateAttainment(halfLifeHr, intervalHr, halfLivesToSS = 5) {
    if (!(halfLifeHr > 0)) {
        return err('kel-must-be-positive', 'Half-life must be positive.', 'halfLifeHr');
    }
    if (!(intervalHr > 0)) {
        return err('tau-non-positive', 'Interval must be positive.', 'intervalHr');
    }
    const ssApproxTimeHr = halfLivesToSS * halfLifeHr;
    const ssDoseNumber = Math.max(1, Math.ceil(ssApproxTimeHr / intervalHr));
    return ok({ ssDoseNumber, ssApproxTimeHr });
}
/**
 * Invert Cmax/MIC formula to solve for the dose that hits a Cmax/MIC target (FE-E9).
 * Rearranged from computePKPD's Cmax expression:
 *   dose = targetCmax × vdL × kel × infusionDurationHr × (1 − e^−kel×intervalHr) / (1 − e^−kel×infusionDurationHr)
 */
export function doseForCmaxMicTarget(kelPerHr, vdL, intervalHr, infusionDurationHr, mic, cmaxMicTarget = 8) {
    if (!(mic > 0)) {
        return err('mic-must-be-positive', 'MIC must be positive.', 'mic');
    }
    if (!(kelPerHr > 0)) {
        return err('kel-must-be-positive', 'Kel must be positive.', 'kelPerHr');
    }
    const targetCmax = cmaxMicTarget * mic;
    const k = kelPerHr;
    const t = infusionDurationHr;
    const numerator = targetCmax * vdL * k * t * (1 - Math.exp(-k * intervalHr));
    const denominator = 1 - Math.exp(-k * t);
    return ok(numerator / denominator);
}
//# sourceMappingURL=pkpd.js.map